import {
  CONSOLIDATED_INDUSTRY_CODE_LIST,
  getIndustryFromIndustryCode,
  safelyGetIndustryLabelFromIndustryCode,
} from '@watershed/shared-universal/industryCodes/industryCodeUtils';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  AutocompleteField,
  AutocompleteFieldNonFormik,
  AutocompleteFieldProps,
} from './AutocompleteField';
import { AutocompleteValue, Popper, PopperProps } from '@mui/material';

import groupBy from 'lodash/groupBy';
import { GICS_CODES } from '@watershed/shared-universal/industryCodes/gics';
import {
  GICS_SECTORS,
  NAICS_SECTORS,
  GICS_INDUSTRY_GROUPS,
} from '@watershed/shared-universal/industryCodes/otherCodes';

export function GrowingPopper(props: PopperProps) {
  return (
    <Popper
      sx={(theme) => ({
        '& .MuiAutocomplete-groupLabel': {
          backgroundColor: theme.palette.grey10,
          color: theme.palette.grey70,
        },
        width: 'fit-content !important',
        maxWidth: '600px',
      })}
      placement="top-start"
      {...props}
    />
  );
}

type NaicsProps = {
  hideCode?: boolean;
  options?: Array<string>;
  grouped?: boolean;
};

/**
 * For searching, we want to include some extra stuff so folks can type in
 * sector or industry group.
 */
export function stringifyCode(option: string): string {
  const isGics = option in GICS_CODES;
  const firstTwo = option.substring(0, 2);

  return JSON.stringify([
    getIndustryFromIndustryCode(option),
    option,
    isGics ? GICS_SECTORS[firstTwo] : NAICS_SECTORS[firstTwo],
    isGics ? GICS_INDUSTRY_GROUPS[option.substring(0, 4)] : '',
  ]);
}

/**
 * Group by sector for either GICS or NAICS codes.
 */
export function groupFunction(option: string): string {
  const firstTwo = option.substring(0, 2);
  const sector =
    option in GICS_CODES ? GICS_SECTORS[firstTwo] : NAICS_SECTORS[firstTwo];
  return sector ?? 'Other';
}

// Typically our default is the formik version (TODO adam)

export default function NaicsAutocompleteNonFormik<
  Multiple extends boolean | undefined = undefined,
  DisabledClearable extends boolean | undefined = undefined,
>({
  value,
  onChange,
  required,
  hideRequired,
  label = 'NAICS Code',
  placeholder = 'Select a NAICS code',
  options = CONSOLIDATED_INDUSTRY_CODE_LIST,
  hideCode = false,
  inline,
  sx,
  disabled,
  grouped = true,
  id = 'naics',
  ...autoCompleteFieldProps
}: NaicsProps & {
  onChange: (
    value: AutocompleteValue<string, Multiple, DisabledClearable, undefined>
  ) => void;
  id?: string;
} & Omit<
    AutocompleteFieldProps<string, Multiple, DisabledClearable>,
    'options' | 'onChange' | 'id'
  >) {
  // Allow searching both by sector & full label
  const filterOptions = createFilterOptions({
    stringify: stringifyCode,
  });

  // Autocomplete wants these to be presorted into groups or it doesn't group in
  // a reasonable way, so we do that here if we're grouping.
  const myOptions = grouped
    ? Object.values(groupBy(options, groupFunction)).flat()
    : options;

  return (
    <AutocompleteFieldNonFormik
      {...autoCompleteFieldProps}
      id={id}
      sx={sx}
      label={label}
      visuallyHideLabel={!label}
      PopperComponent={GrowingPopper}
      placeholder={placeholder}
      options={myOptions}
      getOptionLabel={(code) =>
        safelyGetIndustryLabelFromIndustryCode(code, !hideCode)
      }
      onChange={(_event, value) => onChange(value)}
      value={value}
      required={required}
      hideRequired={hideRequired}
      groupBy={grouped ? groupFunction : undefined}
      filterOptions={filterOptions}
      fsUnmask
      inline={inline}
      disabled={disabled}
    />
  );
}

export function NaicsAutocompleteField<
  Multiple extends boolean | undefined = undefined,
  DisabledClearable extends boolean | undefined = undefined,
>({
  id,
  label = 'NAICS Code',
  grouped = false,
  hideCode = false,
  options = CONSOLIDATED_INDUSTRY_CODE_LIST,
  placeholder = 'Type to search...',
  ...autocompleteFieldProps
}: NaicsProps &
  Omit<
    AutocompleteFieldProps<string, Multiple, DisabledClearable>,
    'options'
  >) {
  // Allow searching both by sector & full label
  const filterOptions = createFilterOptions({
    stringify: stringifyCode,
  });

  // Autocomplete wants these to be presorted into groups or it doesn't group in
  // a reasonable way, so we do that here.
  const myOptions = grouped
    ? Object.values(groupBy(options, groupFunction)).flat()
    : options;

  return (
    <AutocompleteField
      {...autocompleteFieldProps}
      id={id}
      label={label}
      placeholder={placeholder}
      PopperComponent={GrowingPopper}
      autoCompleteOffPrettyPlease
      options={myOptions}
      getOptionLabel={(code) =>
        safelyGetIndustryLabelFromIndustryCode(code, !hideCode)
      }
      groupBy={grouped ? groupFunction : undefined}
      filterOptions={filterOptions}
      fsUnmask
    />
  );
}
